// Host capacity: CPU and memory over time (one % axis), and meters for CPU, memory and disk
// with what the organisations' quotas reserve. From GET /api/host/capacity.
import { Cpu } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";

import { WidgetCard } from "../ui/Card";
import { Meter } from "../ui/Meter";
import { capacityQuery, type HostCapacity } from "./api";
import { gibOf, hourMinute, sizePair, uptime } from "./format";
import { CapacityChart } from "./lazyCharts";
import { QueryState } from "./QueryState";
import { formatNumber, useT } from "../i18n";

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
      <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
      {label}
    </span>
  );
}

function Body({ cap }: { cap: HostCapacity }) {
  const t = useT().dashboard.capacity;
  const cpuShare = cap.cpu_total > 0 ? Math.round((cap.cpu_used / cap.cpu_total) * 100) / 100 : 0;
  const [memUsed, memTotal, memUnit] = sizePair(cap.memory_used_bytes, cap.memory_total_bytes);
  const [diskUsed, diskTotal, diskUnit] = sizePair(cap.disk_used_bytes, cap.disk_total_bytes);
  const data = cap.samples.map((s) => ({ t: hourMinute(s.at), cpu: s.cpu_pct, mem: s.memory_pct }));
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_220px]">
      <div className="h-[220px]">
        {data.length > 1 ? (
          <CapacityChart data={data} />
        ) : (
          <div className="flex h-full items-center justify-center rounded-lg bg-gray-50 text-[13px] text-gray-500 dark:bg-gray-100/40">
            {t.chartFills}
          </div>
        )}
      </div>
      <div className="flex flex-col justify-center gap-5">
        <Meter
          label={t.cpu}
          used={Math.round(cap.cpu_used * 10) / 10}
          total={cap.cpu_total}
          unit={t.cores}
          detail={t.cpuDetail(formatNumber(cpuShare, { style: "percent" }), cap.reserved.cpu)}
        />
        <Meter label={t.memory} used={memUsed} total={memTotal} unit={memUnit} detail={t.reserve(gibOf(cap.reserved.memory_bytes))} />
        <Meter label={t.disk} used={diskUsed} total={diskTotal} unit={diskUnit} detail={t.reserve(gibOf(cap.reserved.disk_bytes))} />
      </div>
    </div>
  );
}

export function HostCapacityWidget({ className }: { className?: string }) {
  const t = useT().dashboard.capacity;
  const query = useQuery(capacityQuery());
  const cap = query.data;
  return (
    <WidgetCard
      className={className}
      title={t.title}
      label={t.title}
      description={[window.location.hostname, cap?.incus_version && t.incus(cap.incus_version), cap && t.up(uptime(cap.uptime_seconds))].filter(Boolean).join(" · ")}
      action={
        cap ? (
          <div className="flex items-center gap-3">
            <LegendDot color="bg-[rgb(var(--chart-1))]" label={t.cpu} />
            <LegendDot color="bg-[rgb(var(--chart-2))]" label={t.memory} />
          </div>
        ) : undefined
      }
    >
      <QueryState query={query} icon={<Cpu weight="duotone" />} empty={t.empty}>
        {(c) => <Body cap={c} />}
      </QueryState>
    </WidgetCard>
  );
}
